import { useCallback, useState } from "react";

import { agentClient } from "../../agentClient";
import type { WireChoice } from "../../protocol/protocol";
import { DECIDE_FAILED, isRedrawRefusal, REVIEW_CHANGES, type CardEdits } from "./overrides";

/** Why the last press did not go through, on the card (args_sha256) it is for. */
interface Notice {
  card: string | null | undefined;
  error: string | null;
  redrawn: string | null;
  /**
   * Refused on the card still shown: the next card keeps the line (the redraw it asked for, or
   * the newer card a CARD_CHANGED meant, its frame late after a reconnect); the one after, or the
   * next press, clears it. Any refusal does, so a line such as a terms one also outlives one
   * unrelated redraw.
   */
  awaiting: boolean;
}

const QUIET = { error: null, redrawn: null, awaiting: false };

/**
 * A choice's decide, as the pick card presses it: the option pressed (until the choice settles),
 * why the last press failed, and `redrawn` (the card's REVIEW_CHANGES line) once a press was
 * refused as "review_changes": the card redrawn at its edits, to check and confirm again, not a
 * failure. Every press names the card it was made on: its edits' `shown`, else `shown` (the
 * choice's args_sha256), so a card redrawn since is refused (CARD_CHANGED) rather than run.
 * Another card clears both lines, except the first card after the refusal (see `awaiting`).
 * `decide` resolves to the decided choice, or null when it failed or was redrawn.
 */
export function useDecide(choiceRef: string | null, shown?: string | null) {
  const [pressed, setPressed] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>({ card: shown, ...QUIET });
  if (notice.card !== shown) {
    setNotice(
      notice.awaiting ? { ...notice, card: shown, awaiting: false } : { card: shown, ...QUIET }
    );
  }
  const decide = useCallback(
    async (optionId: string, edits?: CardEdits): Promise<WireChoice | null> => {
      if (!choiceRef) return null;
      const on = edits?.shown ?? shown;
      setPressed(optionId);
      setNotice((prev) => ({ card: prev.card, ...QUIET }));
      try {
        const card = edits ?? (shown ? { shown } : undefined);
        return await agentClient.decide(choiceRef, optionId, undefined, undefined, card);
      } catch (reason) {
        setPressed(null);
        const redrawn = isRedrawRefusal(reason);
        const message = (reason as { message?: string } | null)?.message;
        setNotice((prev) => ({
          card: prev.card,
          error: redrawn ? null : message || DECIDE_FAILED,
          redrawn: redrawn ? REVIEW_CHANGES : null,
          awaiting: prev.card === on,
        }));
        return null;
      }
    },
    [choiceRef, shown]
  );
  return { decide, pressed, error: notice.error, redrawn: notice.redrawn };
}
